import { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Product Video Component
 * Displays product videos with lazy loading (click-to-play)
 * Supports YouTube and Vimeo URLs
 */
function ProductVideo({ videoUrl, videoThumbnailUrl, productName }) {
  const [isPlaying, setIsPlaying] = useState(false);

  // Extract video ID and platform from URL
  const getVideoInfo = (url) => {
    if (!url) return null;

    // YouTube patterns
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
      /youtube\.com\/embed\/([^?\s]+)/
    ];

    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          platform: 'youtube',
          id: match[1],
          embedUrl: `https://www.youtube.com/embed/${match[1]}?autoplay=1`
        };
      }
    }

    // Vimeo patterns
    const vimeoPatterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/
    ];

    for (const pattern of vimeoPatterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          platform: 'vimeo',
          id: match[1],
          embedUrl: `https://player.vimeo.com/video/${match[1]}?autoplay=1`
        };
      }
    }

    return null;
  };

  // Get thumbnail URL
  const getThumbnailUrl = (videoInfo) => {
    // Use custom thumbnail if provided
    if (videoThumbnailUrl) {
      return videoThumbnailUrl;
    }

    // Extract YouTube thumbnail if it's a YouTube video
    if (videoInfo?.platform === 'youtube') {
      return `https://img.youtube.com/vi/${videoInfo.id}/maxresdefault.jpg`;
    }

    // For Vimeo, we'd need an API call to get thumbnail, so return a placeholder
    return '/assets/img/video-placeholder.jpg';
  };

  const videoInfo = getVideoInfo(videoUrl);

  if (!videoInfo) {
    return null; // Don't render if video URL is invalid
  }

  const thumbnailUrl = getThumbnailUrl(videoInfo);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div className="product-video-container mt-4 mb-4">
      <h5 className="text-center text-lg-left mb-3">Product Video</h5>
      <div className="video-wrapper" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
        {!isPlaying ? (
          <div
            className="video-thumbnail"
            onClick={handlePlay}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              backgroundImage: `url(${thumbnailUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              className="play-button"
              style={{
                width: '80px',
                height: '80px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <i
                className="fas fa-play"
                style={{
                  color: 'white',
                  fontSize: '30px',
                  marginLeft: '5px'
                }}
              ></i>
            </div>
          </div>
        ) : (
          <iframe
            src={videoInfo.embedUrl}
            title={`${productName} Video`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          ></iframe>
        )}
      </div>
    </div>
  );
}

ProductVideo.propTypes = {
  videoUrl: PropTypes.string,
  videoThumbnailUrl: PropTypes.string,
  productName: PropTypes.string
};

ProductVideo.defaultProps = {
  videoUrl: null,
  videoThumbnailUrl: null,
  productName: 'Product'
};

export default ProductVideo;
